import { FormGenerator } from "@gsa/afp-shared-form-utils";
import { Button, useModal } from "@gsa/afp-component-library";
import { generateContractingReviewVendorForm, generateAreqReviewDefaultValues, generateEngineeringApprovalVendorForm } from "./contracting-review-vendor-form";
import { contractingReviewVendorSchema, engineeringApprovalSchema } from "./contracting-review-vendor-schema";
import { UploadQuoteModal } from "./upload-quote-modal";
import { useSelector } from "react-redux";
import { useCreateRequisitionAttachment, useCreateVendorQuoteDocument, useDeleteRequisitionAttachment, useDeleteVendorQuote, useDeleteVendorQuoteDocument, useUpdateRequisitionAttachment } from "../../../../../../requests/leasing";
import { AREQVendorQuoteAttachments } from "../contracting-review-actions";
import { useRef, useState } from "react";
import { useCreateMetadataRecord, useDeleteMetadataRecord } from "../../../../../../requests/vehicle-referral";
import axios from 'axios';
import { useDispatch } from "react-redux";
import { setVendorQuotes } from "../../../../../../reducers/leasing";
import { RequisitionStatus } from "../../../../../ReviewDetails/RequisitionDetailsUtils";

export default function ContractingReviewVendor({
    vendorQuote,
    index,
    attachmentTypes,
    vendors,
    contracts,
    makeModels,
    removeVendorQuote,
    formRef,
}) {
    const requisitionId = useSelector((state) => state.leasingReducer.requisitionId);
    const requisitionStatus = useSelector((state) => state.leasingReducer.requisitionStatus);
    const [createMetadataRecord, { loading: uploadingMetadata }] = useCreateMetadataRecord();
    const vendorQuotes = useSelector((state) => state.leasingReducer.vendorQuotes) || [];
    const dispatch = useDispatch();
    const [deleteMetadataRecord, { loading: deletingMetadataRecord }] = useDeleteMetadataRecord();
    const [createRequisitionAttachment, { loading: uploading }] = useCreateRequisitionAttachment();
    const [deleteRequisitionAttachment, { loading: deletingRequisitionAttachment }] = useDeleteRequisitionAttachment();
    const [updateRequisitionAttachment, { loading: saving }] = useUpdateRequisitionAttachment();
    const [createVendorQuoteDocument, { loading: creatingVendorQuoteDocument }] = useCreateVendorQuoteDocument();
    const [deleteVendorQuoteDocument, { loading: deletingVendorQuoteDocument }] = useDeleteVendorQuoteDocument();
    const [deleteVendorQuote, { loading: loadingDeleteVendorQuote }] = useDeleteVendorQuote();
    const vendorQuoteAttachmentType = attachmentTypes.find(type => type.label === AREQVendorQuoteAttachments.req_vendor_quote)?.value;
    const vendorSupportingDocAttachmentType = attachmentTypes.find(type => type.label === AREQVendorQuoteAttachments.req_vendor_supporting_doc)?.value;
    const [vendorQuoteUploadError, setVendorQuoteUploadError] = useState(null);

    const uploadQuoteModal = useModal();

    const uploadVendorQuote = async (data) => {
        try {
            const metadataInput = {
                name: data.file.name,
                size: data.file.size,
                fileMimeType: data.file.type,
                description: 'vendor quote',
                fileLocation: `requisition_attachments/${new Date().getFullYear()}/${requisitionId}/${data.file.name}`,
                linkedEntities: {
                    vendorQuoteId: vendorQuote.requisitionVendorQuoteId,
                    requisitionId
                },
                docSource: 'UserUploaded',
                docStore: window.AFP_CONFIG.attachment_bucket,
            };

            let metadata = null;
            try {
                const metadataRecord = await createMetadataRecord({
                    variables: { input: metadataInput },
                });

                metadata = metadataRecord.data;
            } catch ({ error }) {
                throw error;
            }

            try {
                await axios.put(metadata.signedUrl, data.file, {
                    headers: {
                        'Content-Type': data.file.type,
                        'Content-Disposition': `attachment; filename="${data.file.name}"`,
                    },
                });
            } catch (error) {
                try {
                    await deleteMetadataRecord({
                        variables: {
                            deleteMetadataRecordId: metadata.id,
                        },
                    });
                } catch ({ error }) {
                    throw error;
                }

                throw error;
            }

            try {
                const { data: attachment } = await createRequisitionAttachment({
                    variables: {
                        input: {
                            requisitionId,
                            name: data.file.name,
                            status: 0,
                            attachmentTypeId: vendorQuoteAttachmentType,
                            description: data.note,
                            size: data.file.size,
                            fileMimeType: data.file.type,
                            metadataId: metadata.id,
                            signedUrl: metadata.signedUrl,
                        },
                    },
                });

                const { data: createdVendorQuote } = await createVendorQuoteDocument({
                    variables: {
                        requisitionAttachmentId: attachment.requisitionAttachmentId,
                        requisitionVendorQuoteId: parseInt(vendorQuote.requisitionVendorQuoteId),
                        isSupportingDocument: false,
                    }
                });

                formRef.current?.setValue(`quote-${index}`, attachment, {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                })
            } catch ({ error }) {
                throw error;
            }
        } catch (error) {
            setVendorQuoteUploadError(error.message || 'An error occurred while uploading the vendor quote.');
            return;
        }

        uploadQuoteModal.closeModal();
    }

    const handleDeleteVendorQuote = async (data) => {
        try {
            await deleteRequisitionAttachment({
                variables: {
                    attachmentId: data.requisitionAttachmentId
                },
            });

            await deleteMetadataRecord({
                variables: {
                    deleteMetadataRecordId: data.metadataId,
                },
            });

            await deleteVendorQuoteDocument({
                variables: {
                    requisitionVendorQuoteId: parseInt(vendorQuote.requisitionVendorQuoteId),
                    requisitionVendorQuoteDocumentId: 0,
                    isSupportingDocument: false,
                }
            });

            formRef.current?.setValue(`quote-${index}`, null, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
            })
        } catch ({ error }) {
            setVendorQuoteUploadError(error.message || 'An error occurred while deleting the vendor quote.');
            return;
        }
    }

    const handleUploadSupportingDocument = async (data, { addFileToList }) => {
        const metadataInput = {
            name: data.file.name,
            size: data.file.size,
            fileMimeType: data.file.type,
            description: 'vendor quote',
            fileLocation: `requisition_attachments/${new Date().getFullYear()}/${requisitionId}/${data.file.name}`,
            linkedEntities: {
                vendorId: vendorQuote.requisitionVendorQuoteId,
                requisitionId
            },
            docSource: 'UserUploaded',
            docStore: window.AFP_CONFIG.attachment_bucket,
        };

        let metadata = null;
        try {
            const metadataRecord = await createMetadataRecord({
                variables: { input: metadataInput },
            });

            metadata = metadataRecord.data;
        } catch ({ error }) {
            throw error;
        }

        try {
            await axios.put(metadata.signedUrl, data.file, {
                headers: {
                    'Content-Type': data.file.type,
                    'Content-Disposition': `attachment; filename="${data.file.name}"`,
                },
            });
        } catch (error) {
            try {
                await deleteMetadataRecord({
                    variables: {
                        deleteMetadataRecordId: metadata.id,
                    },
                });
            } catch ({ error }) {
                throw error;
            }

            throw error;
        }

        try {
            const { data: attachment } = await createRequisitionAttachment({
                variables: {
                    input: {
                        requisitionId,
                        name: data.file.name,
                        status: 0,
                        attachmentTypeId: vendorSupportingDocAttachmentType,
                        description: data.note,
                        size: data.file.size,
                        fileMimeType: data.file.type,
                        metadataId: metadata.id,
                        signedUrl: metadata.signedUrl,
                    },
                },
            });

            const { data: createdVendorQuote } = await createVendorQuoteDocument({
                variables: {
                    requisitionAttachmentId: attachment.requisitionAttachmentId,
                    requisitionVendorQuoteId: parseInt(vendorQuote.requisitionVendorQuoteId),
                    isSupportingDocument: true,
                }
            });

            addFileToList({
                ...attachment,
                note: attachment.description,
                id: attachment.requisitionAttachmentId,
            });
        } catch ({ error }) {
            throw error;
        }
    }
    
    const handleSave = async (data, { updateFileInList }) => {
        try {
            const { data: attachment } = await updateRequisitionAttachment({
                variables: {
                    attachmentId: data.file.id,
                    input: {
                        attachmentTypeId: data.category,
                        description: data.note,
                    },
                },
            });

            updateFileInList({
                ...attachment,
                note: attachment.description,
                id: attachment.requisitionAttachmentId,
            });
        } catch ({ error }) {
            throw error;
        }
    };

    const handleDelete = async (data, { removeFileFromList }) => {
        try {
            await deleteRequisitionAttachment({
                variables: {
                    attachmentId: data.id,
                },
            });

            await deleteMetadataRecord({
                variables: {
                    deleteMetadataRecordId: data.metadataId,
                },
            });

            await deleteVendorQuoteDocument({
                variables: {
                    requisitionVendorQuoteId: parseInt(vendorQuote.requisitionVendorQuoteId),
                    requisitionAttachmentId: data.id,
                    isSupportingDocument: true,
                }
            });

            removeFileFromList({
                ...data,
                id: data.id,
            });
        } catch ({ error }) {
            throw error;
        }
    };

    const handleEditAction = (data, { openFileModal }) => {
        openFileModal();
    };

    const handleDeleteAction = async (data, { openConfirmDeleteModal }) => {
        openConfirmDeleteModal();
    };

    let schema = () => { };
    if (requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW) {
        schema = contractingReviewVendorSchema;
    } else if (requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL) {
        schema = engineeringApprovalSchema;
    }

    const defaultValues = generateAreqReviewDefaultValues({
        vendorQuote,
        index,
        attachmentTypes,
        contracts,
        makeModels,
        vendors,
    });

    let content = null;
    if (requisitionStatus === RequisitionStatus.CONTRACTING_REVIEW) {
        content = generateContractingReviewVendorForm({
            index,
            uploadQuoteModal,
            handleUpload: handleUploadSupportingDocument,
            handleSave,
            handleDelete,
            handleEditAction,
            handleDeleteAction,
            handleDeleteVendorQuote,
            uploading: uploading || uploadingMetadata || deletingRequisitionAttachment,
            deleting: deletingRequisitionAttachment || deletingMetadataRecord,
            saving,
            vendors: vendors.map(vendor => ({
                value: vendor.id,
                label: vendor.vendorName,
            })),
            makeModels,
            vendorQuote,
            engineeringApproval: requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL,
        });
    } else if (requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL || requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL) {
        content = generateEngineeringApprovalVendorForm({
            index,
            uploadQuoteModal,
            handleUpload: handleUploadSupportingDocument,
            handleSave,
            handleDelete,
            handleEditAction,
            handleDeleteAction,
            handleDeleteVendorQuote,
            uploading: uploading || uploadingMetadata || deletingRequisitionAttachment,
            deleting: deletingRequisitionAttachment || deletingMetadataRecord,
            saving,
            vendors: vendors.map(vendor => ({
                value: vendor.id,
                label: vendor.vendorName,
            })),
            makeModels,
            vendorQuote,
            contractingApproval: requisitionStatus === RequisitionStatus.CONTRACTING_APPROVAL
        });
    }

    return (
        <>
            <FormGenerator
                ref={formRef}
                className='contracting-review-vendor-form'
                id={`contracting-review-vendor-${index}`}
                schema={schema(index)}
                content={content}
                defaultValues={defaultValues}
                useFormProps={{
                    mode: 'all',
                }}
            />

            <UploadQuoteModal
                isOpen={uploadQuoteModal.isOpen}
                handleClose={uploadQuoteModal.closeModal}
                handleSubmit={uploadVendorQuote}
                error={null}
                saving={uploading}
            />
        </>
    )
}